import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Edit, Plus } from "lucide-react";
import AuthProviderForm from "@/components/forms/auth/AuthProviderForm";
import {
    useCreateAuthProviderMutation,
    useUpdateAuthProviderMutation,
} from "@/api/mutations/admin/AuthProviderMutations";
import { formatProviderName } from "@shared/utils/format-provider-name";

interface AuthProviderDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    provider?: any; // Optional for create mode
    mode?: "create" | "edit";
}

export function AuthProviderDialog({ open, onOpenChange, provider, mode = "edit" }: AuthProviderDialogProps) {
    const [error, setError] = useState("");

    const createMutation = useCreateAuthProviderMutation(undefined, setError);
    const updateMutation = useUpdateAuthProviderMutation(undefined, setError);

    const isCreateMode = mode === "create";

    const handleSubmit = (data: any) => {
        if (isCreateMode) {
            // Create new provider
            const providerType = data.AUTH_PROVIDER || "microsoft";
            const displayName = formatProviderName(providerType);
            createMutation.mutate({
                provider: providerType,
                display_name: displayName,
                config: data,
            });
        } else {
            // Update existing provider
            if (!provider) return;

            const newProviderType = data.AUTH_PROVIDER || provider.provider;
            const newDisplayName = formatProviderName(newProviderType);
            updateMutation.mutate({
                id: provider.id,
                data: {
                    provider: newProviderType,
                    display_name: newDisplayName,
                    config: data,
                },
            });
        }
    };

    // Close dialog on successful create/update
    useEffect(() => {
        if (createMutation.isSuccess || updateMutation.isSuccess) {
            onOpenChange(false);
            // Reset the mutation states when dialog closes
            createMutation.reset();
            updateMutation.reset();
        }
    }, [createMutation.isSuccess, updateMutation.isSuccess, onOpenChange, createMutation, updateMutation]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle className="flex items-center gap-2">
                            {isCreateMode ? (
                                <>
                                    <Plus className="h-5 w-5" />
                                    Create New Auth Provider
                                </>
                            ) : (
                                <>
                                    <Edit className="h-5 w-5" />
                                    Edit {provider?.display_name || formatProviderName(provider?.provider || "")} Provider
                                </>
                            )}
                        </DialogTitle>
                        {!isCreateMode && provider && (
                            <Badge variant={provider.enabled ? "default" : "secondary"}>
                                {provider.enabled ? "Enabled" : "Disabled"}
                            </Badge>
                        )}
                    </div>
                </DialogHeader>

                <div className="space-y-4">
                    {error && <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}
                    <AuthProviderForm
                        initialData={isCreateMode ? {} : { ...provider?.config }}
                        onSubmit={handleSubmit}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}
